import { i18n, type SupportedLanguage } from "@/services/locale";
import { useAiNotificationStore } from "@/stores/ai-notification";
import type { AiAssistant } from "@/types/ai/Assistant";
import { Assistant } from "./Assistant";

/**
 * App Assistant - Helps users with app navigation, help, and settings
 */
export class AppAssistant extends Assistant {
  private language: SupportedLanguage;

  constructor() {
    super();
    this.language = i18n.getCurrentLanguage();
  }

  config(): AiAssistant {
    return {
      systemPrompt: this.getSystemPrompt(),
      model: "gpt-4o-mini",
      temperature: 0.3,
      maxTokens: 500,
      language: this.language,
    };
  }

  getSystemPrompt(): string {
    return `
You are an AI assistant that helps users navigate Atlasio and understand how to use the app.

## YOUR ROLE
Help users with:
- App navigation and features
- How to use different parts of the app
- Settings and preferences
- General help and guidance

## AVAILABLE ACTIONS
You can provide guidance and explanations, but you cannot directly change settings or navigate. Guide users on how to do these things.

## RESPONSE STYLE
- Be helpful and conversational
- Provide clear, step-by-step guidance
- Explain features and how to use them
- Always respond in the selected i18n language (see CRITICAL LANGUAGE REQUIREMENT above)

## EXAMPLES
User: "How do I add data to a workspace?"
→ Explain the process of adding datasets or files to a workspace

User: "What can you do?"
→ Explain your capabilities and how you can help

User: "Navigate to discover"
→ Guide them to click the Discover link in the navigation
    `;
  }

  async execute(prompt: string): Promise<void> {
    const { showNotification } = useAiNotificationStore.getState();

    const response = await Assistant.sendPrompt<void>(prompt, this.config());

    if (response.error) {
      showNotification(response.error, true);
      return;
    }

    if (response.reply) {
      showNotification(response.reply, false);
    }

    if (response.data) {
      // TODO: Implement app logic
    }
  }
}
